import {
  forwardRef,
  useRef,
  FunctionComponent,
  PropsWithoutRef,
  Ref,
  MouseEvent,
  MouseEventHandler,
  useEffect
} from 'react';
import styled from 'styled-components';

import { BaseProps, ForwardProps } from '../../types';
import { defaultThemeProp } from '../../theme';
import { useConsolidatedRef } from '../../hooks';

import { ButtonProps } from './Button';

export interface BareButtonProps extends BaseProps {
  /** Text or content for the Button. */
  children: ButtonProps['children'];
  /**
   * Determines if the Button will be disabled.
   * @default false
   */
  disabled?: ButtonProps['disabled'];
  /** A location to navigate to. Passing an href will render a Link styled as a Button. */
  href?: ButtonProps['href'];
  /**
   * Controls the behavior of a Button within a Form.
   * @default "button"
   */
  type?: ButtonProps['type'];
  /** Ref forwarded to the wrapping element. */
  ref?: ButtonProps['ref'];
}

export const StyledBareButton = styled.button`
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  user-select: none;

  & + & {
    margin-inline-start: ${props => props.theme.base.spacing};
  }
`;

StyledBareButton.defaultProps = defaultThemeProp;

const BareButton: FunctionComponent<BareButtonProps & ForwardProps> = forwardRef(
  (
    {
      type = 'button',
      disabled = false,
      href,
      ...restProps
    }: PropsWithoutRef<BareButtonProps> & ForwardProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const mouseDownEvent = useRef<MouseEvent>();
    const buttonRef = useConsolidatedRef(ref);

    useEffect(() => {
      return () => {
        mouseDownEvent.current = undefined;
      };
    }, []);

    return (
      <StyledBareButton
        ref={buttonRef}
        as={href ? ('a' as any) : undefined}
        type={href ? undefined : type}
        disabled={disabled}
        {...restProps}
        href={href}
        onMouseDown={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
          (restProps.onMouseDown as MouseEventHandler | undefined)?.(e);
          e.persist();
          mouseDownEvent.current = e;
        }}
        onMouseUp={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
          const shouldFocus = mouseDownEvent.current && !mouseDownEvent.current.defaultPrevented;
          mouseDownEvent.current = undefined;

          if (shouldFocus && buttonRef.current !== document.activeElement) {
            buttonRef.current?.focus();
          }

          return (restProps.onMouseUp as MouseEventHandler | undefined)?.(e);
        }}
      />
    );
  }
);

export default BareButton;
